import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  Users, 
  Car, 
  Calendar, 
  DollarSign,
  Trash2,
  Shield,
  ShieldOff,
  Loader2,
  TrendingUp,
  UserPlus,
  ChevronLeft,
  X,
  CheckCircle,
  XCircle,
  Clock,
  Edit2,
  Save,
  Eye,
  EyeOff,
  Pencil,
  BookOpen,
  CreditCard
} from "lucide-react";
import BrandManual from "./BrandManual";
import StripeConnectAdmin from "./admin/StripeConnectAdmin";

import { API } from "../utils/api";

export default function Admin() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState("stats");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [spots, setSpots] = useState([]);
  const [bookings, setBookings] = useState([]);
  
  // Estado para el modal de edición de usuario
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "",
    new_password: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [savingUser, setSavingUser] = useState(false);

  // Estado para el modal de edición de plaza
  const [editingSpot, setEditingSpot] = useState(null);
  const [spotForm, setSpotForm] = useState({
    title: "",
    description: "",
    address: "",
    city: "",
    price_hour: "",
    price_day: "",
    price_month: ""
  });
  const [savingSpot, setSavingSpot] = useState(false);

  // Multi-selección para bulk delete
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedSpotIds, setSelectedSpotIds] = useState([]);
  const [bulkDeleteTarget, setBulkDeleteTarget] = useState(null); // "users" | "spots" | null
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleUserSelection = (id) => {
    setSelectedUserIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleSpotSelection = (id) => {
    setSelectedSpotIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleAllUsers = () => {
    setSelectedUserIds(prev => prev.length === users.length ? [] : users.map(u => u.id));
  };
  const toggleAllSpots = () => {
    setSelectedSpotIds(prev => prev.length === spots.length ? [] : spots.map(s => s.id));
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      if (bulkDeleteTarget === "users") {
        const res = await axios.post(`${API}/admin/users/bulk-delete`, { ids: selectedUserIds });
        toast.success(res.data.message);
        if (res.data.skipped_self) {
          toast.info("Se ha omitido tu propio usuario de la lista");
        }
        setSelectedUserIds([]);
      } else if (bulkDeleteTarget === "spots") {
        const res = await axios.post(`${API}/admin/spots/bulk-delete`, { ids: selectedSpotIds });
        toast.success(res.data.message);
        setSelectedSpotIds([]);
      }
      setBulkDeleteTarget(null);
      await loadData();
    } catch (error) {
      const msg = error.response?.data?.detail || "Error al eliminar";
      toast.error(msg);
    } finally {
      setBulkDeleting(false);
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, spotsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/spots`),
        axios.get(`${API}/admin/bookings`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setSpots(spotsRes.data.spots);
      setBookings(bookingsRes.data.bookings);
    } catch (error) {
      console.error("Error loading admin data:", error);
      toast.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate("/login");
      } else if (user.role !== "admin") {
        toast.error("Acceso denegado. Se requiere rol de administrador");
        navigate("/dashboard");
      } else {
        loadData();
      }
    }
  }, [user, authLoading, navigate, loadData]);

  const handleEditUser = (u) => {
    setEditingUser(u);
    setEditForm({
      name: u.name || "",
      email: u.email || "",
      phone: u.phone || "",
      role: u.role || "user",
      new_password: ""
    });
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    
    setSavingUser(true);
    try {
      const payload = {
        name: editForm.name,
        email: editForm.email || null,
        phone: editForm.phone || null,
        role: editForm.role
      };
      
      if (editForm.new_password) {
        payload.new_password = editForm.new_password;
      }
      
      await axios.put(`${API}/admin/users/${editingUser.id}`, payload);
      toast.success("Usuario actualizado correctamente");
      setEditingUser(null);
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || "Error al actualizar usuario";
      toast.error(message);
    } finally {
      setSavingUser(false);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!confirm(`¿Seguro que quieres eliminar al usuario @${username}?`)) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success("Usuario eliminado");
      setUsers(users.filter(u => u.id !== userId));
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar usuario");
    }
  };

  const handleToggleAdmin = async (userId, currentRole) => {
    const newRole = currentRole === "admin" ? "user" : "admin";
    try {
      await axios.put(`${API}/admin/users/${userId}/role?role=${newRole}`);
      toast.success(`Rol actualizado a ${newRole}`);
      setUsers(users.map(u => u.id === userId ? {...u, role: newRole} : u));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar rol");
    }
  };

  const handleEditSpot = (spot) => {
    setEditingSpot(spot);
    setSpotForm({
      title: spot.title || "",
      description: spot.description || "",
      address: spot.address || "",
      city: spot.city || "",
      price_hour: spot.price_hour || "",
      price_day: spot.price_day || "",
      price_month: spot.price_month || ""
    });
  };

  const handleSaveSpot = async () => {
    if (!editingSpot) return;
    
    setSavingSpot(true);
    try {
      await axios.put(`${API}/admin/spots/${editingSpot.id}`, {
        title: spotForm.title,
        description: spotForm.description,
        address: spotForm.address,
        city: spotForm.city,
        price_hour: parseFloat(spotForm.price_hour) || 0,
        price_day: parseFloat(spotForm.price_day) || null,
        price_month: parseFloat(spotForm.price_month) || null
      });
      toast.success("Plaza actualizada correctamente");
      setEditingSpot(null);
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || "Error al actualizar plaza";
      toast.error(message);
    } finally {
      setSavingSpot(false);
    }
  };

  const handleDeleteSpot = async (spotId, title) => {
    if (!confirm(`¿Seguro que quieres eliminar la plaza "${title}"?`)) return;
    
    try {
      await axios.delete(`${API}/admin/spots/${spotId}`);
      toast.success("Plaza eliminada");
      setSpots(spots.filter(s => s.id !== spotId));
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar plaza");
    }
  };

  const handleUpdateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/status?status=${newStatus}`);
      toast.success(`Estado actualizado a ${newStatus}`);
      setBookings(bookings.map(b => b.id === bookingId ? {...b, status: newStatus} : b));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cambiar estado");
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm("¿Eliminar esta reserva permanentemente? Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`);
      toast.success("Reserva eliminada");
      setBookings(prev => prev.filter(b => b.id !== bookingId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar reserva");
    }
  };

  if (authLoading || loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  if (!user || user.role !== "admin") return null;

  const tabs = [
    { id: "stats", label: "Estadísticas", icon: TrendingUp },
    { id: "users", label: "Usuarios", icon: Users, count: stats?.total_users },
    { id: "spots", label: "Plazas", icon: Car, count: stats?.total_spots },
    { id: "bookings", label: "Reservas", icon: Calendar, count: stats?.total_bookings },
    { id: "stripe", label: "Stripe Connect", icon: CreditCard },
    { id: "manuals", label: "Manuales", icon: BookOpen },
  ];

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500/20 text-yellow-400",
      confirmed: "bg-blue-500/20 text-blue-400",
      completed: "bg-green-500/20 text-green-400",
      cancelled: "bg-red-500/20 text-red-400"
    };
    const labels = {
      pending: "Pendiente",
      confirmed: "Confirmada",
      completed: "Completada",
      cancelled: "Cancelada"
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div className="pt-20 min-h-screen" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Shield className="w-8 h-8 text-violet-400" />
              Panel de Administración
            </h1>
            <p className="text-gray-400 mt-1">Gestiona usuarios, plazas y reservas</p>
          </div>
          <Button onClick={loadData} variant="outline" className="border-white/10 text-gray-300 hover:bg-white/5">
            Actualizar datos
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              data-testid={`admin-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? "bg-violet-500 text-white"
                  : "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count !== undefined && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? "bg-white/20" : "bg-white/10"
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="dashboard-card">
          {/* Stats Tab */}
          {activeTab === "stats" && stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-violet-500/20 to-violet-600/10 rounded-xl p-6 border border-violet-500/20">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-violet-500/20 rounded-lg">
                    <Users className="w-6 h-6 text-violet-400" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Total Usuarios</p>
                    <p className="text-3xl font-bold text-white">{stats.total_users}</p>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2 text-sm">
                  <UserPlus className="w-4 h-4 text-green-400" />
                  <span className="text-green-400">+{stats.new_users_this_month}</span>
                  <span className="text-gray-500">este mes</span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-xl p-6 border border-blue-500/20">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-500/20 rounded-lg">
                    <Car className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Plazas Publicadas</p>
                    <p className="text-3xl font-bold text-white">{stats.total_spots}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-xl p-6 border border-green-500/20">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-500/20 rounded-lg">
                    <Calendar className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Total Reservas</p>
                    <p className="text-3xl font-bold text-white">{stats.total_bookings}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 rounded-xl p-6 border border-amber-500/20">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-amber-500/20 rounded-lg">
                    <DollarSign className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Ingresos Totales</p>
                    <p className="text-3xl font-bold text-white">{stats.total_revenue.toFixed(2)}€</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === "users" && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="py-3 px-4 w-10">
                      <Checkbox
                        checked={users.length > 0 && selectedUserIds.length === users.length}
                        onCheckedChange={toggleAllUsers}
                        data-testid="admin-users-select-all"
                        aria-label="Seleccionar todos"
                      />
                    </th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Usuario</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Email</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Rol</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Registro</th>
                    <th className="text-right py-3 px-4 text-gray-400 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const selected = selectedUserIds.includes(u.id);
                    return (
                    <tr
                      key={u.id}
                      className={`border-b border-white/5 transition-colors ${selected ? "bg-violet-500/10" : "hover:bg-white/5"}`}
                    >
                      <td className="py-3 px-4">
                        <Checkbox
                          checked={selected}
                          onCheckedChange={() => toggleUserSelection(u.id)}
                          data-testid={`admin-user-checkbox-${u.id}`}
                          aria-label={`Seleccionar ${u.username}`}
                        />
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center">
                            {u.avatar ? (
                              <img src={u.avatar.startsWith('http') ? u.avatar : `${window.location.origin}${u.avatar}`} alt="" className="w-full h-full rounded-full object-cover" />
                            ) : (
                              <span className="text-sm font-bold text-violet-400">{u.name?.charAt(0).toUpperCase()}</span>
                            )}
                          </div>
                          <div>
                            <p className="text-white font-medium">{u.name}</p>
                            <p className="text-gray-500 text-sm">{u.username ? `@${u.username}` : ''}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-300">{u.email || "-"}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          u.role === "admin" ? "bg-violet-500/20 text-violet-400" : "bg-gray-500/20 text-gray-400"
                        }`}>
                          {u.role === "admin" ? "Admin" : "Usuario"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-400 text-sm">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('es-ES') : "-"}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditUser(u)}
                            className="text-blue-400 hover:bg-blue-500/10"
                            title="Editar usuario"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleToggleAdmin(u.id, u.role)}
                            className={u.role === "admin" ? "text-violet-400 hover:bg-violet-500/10" : "text-gray-400 hover:bg-white/5"}
                            title={u.role === "admin" ? "Quitar admin" : "Hacer admin"}
                          >
                            {u.role === "admin" ? <ShieldOff className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteUser(u.id, u.username)}
                            className="text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              {users.length === 0 && (
                <p className="text-center text-gray-500 py-8">No hay usuarios</p>
              )}
            </div>
          )}

          {/* Spots Tab */}
          {activeTab === "spots" && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="py-3 px-4 w-10">
                      <Checkbox
                        checked={spots.length > 0 && selectedSpotIds.length === spots.length}
                        onCheckedChange={toggleAllSpots}
                        data-testid="admin-spots-select-all"
                        aria-label="Seleccionar todas"
                      />
                    </th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Plaza</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Propietario</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Ubicación</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Precio/día</th>
                    <th className="text-right py-3 px-4 text-gray-400 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {spots.map((spot) => {
                    const selected = selectedSpotIds.includes(spot.id);
                    return (
                    <tr
                      key={spot.id}
                      className={`border-b border-white/5 transition-colors ${selected ? "bg-violet-500/10" : "hover:bg-white/5"}`}
                    >
                      <td className="py-3 px-4">
                        <Checkbox
                          checked={selected}
                          onCheckedChange={() => toggleSpotSelection(spot.id)}
                          data-testid={`admin-spot-checkbox-${spot.id}`}
                          aria-label={`Seleccionar ${spot.title}`}
                        />
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-white font-medium">{spot.title}</p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-gray-300">{spot.owner?.name || "-"}</p>
                        <p className="text-gray-500 text-sm">@{spot.owner?.username || "-"}</p>
                      </td>
                      <td className="py-3 px-4 text-gray-300">
                        {spot.city}, {spot.country}
                      </td>
                      <td className="py-3 px-4 text-white font-medium">
                        {spot.price_day}€
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditSpot(spot)}
                            className="text-violet-400 hover:bg-violet-500/10"
                            title="Editar plaza"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteSpot(spot.id, spot.title)}
                            className="text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              {spots.length === 0 && (
                <p className="text-center text-gray-500 py-8">No hay plazas publicadas</p>
              )}
            </div>
          )}

          {/* Bookings Tab */}
          {activeTab === "bookings" && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Reserva</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Usuario</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Plaza</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Precio</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Estado</th>
                    <th className="text-right py-3 px-4 text-gray-400 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => (
                    <tr key={booking.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-4">
                        <p className="text-white font-medium text-sm">#{booking.id?.slice(-6)}</p>
                        <p className="text-gray-500 text-xs">
                          {booking.start_time ? new Date(booking.start_time).toLocaleDateString('es-ES') : "-"}
                        </p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-gray-300">{booking.user?.name || "-"}</p>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-gray-300">{booking.spot?.title || "-"}</p>
                      </td>
                      <td className="py-3 px-4 text-white font-medium">
                        {booking.total_price?.toFixed(2) || 0}€
                      </td>
                      <td className="py-3 px-4">
                        {getStatusBadge(booking.status)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleUpdateBookingStatus(booking.id, "confirmed")}
                            className="text-blue-400 hover:bg-blue-500/10"
                            title="Confirmar"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleUpdateBookingStatus(booking.id, "completed")}
                            className="text-green-400 hover:bg-green-500/10"
                            title="Completar"
                          >
                            <Clock className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleUpdateBookingStatus(booking.id, "cancelled")}
                            className="text-red-400 hover:bg-red-500/10"
                            title="Cancelar"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteBooking(booking.id)}
                            className="text-red-500 hover:bg-red-500/15"
                            title="Eliminar permanentemente"
                            data-testid={`admin-booking-delete-${booking.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {bookings.length === 0 && (
                <p className="text-center text-gray-500 py-8">No hay reservas</p>
              )}
            </div>
          )}

          {/* Stripe Connect */}
          {activeTab === "stripe" && (
            <StripeConnectAdmin />
          )}

          {/* Manuales */}
          {activeTab === "manuals" && (
            <BrandManual />
          )}
        </div>
      </div>

      {/* Modal de edición de usuario */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#231f30] rounded-2xl border border-white/10 w-full max-w-md shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div>
                <h2 className="text-xl font-bold text-white">Editar Usuario</h2>
                <p className="text-gray-400 text-sm">@{editingUser.username}</p>
              </div>
              <button
                onClick={() => setEditingUser(null)}
                className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <div className="p-6 space-y-4">
              <div>
                <Label className="text-gray-300">Nombre</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  placeholder="Nombre completo"
                  className="bg-[#15121e] border-white/10 text-white"
                />
              </div>

              <div>
                <Label className="text-gray-300">Email</Label>
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  placeholder="correo@ejemplo.com"
                  className="bg-[#15121e] border-white/10 text-white"
                />
              </div>

              <div>
                <Label className="text-gray-300">Teléfono</Label>
                <Input
                  value={editForm.phone}
                  onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                  placeholder="+34 600 000 000"
                  className="bg-[#15121e] border-white/10 text-white"
                />
              </div>

              <div>
                <Label className="text-gray-300">Rol</Label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                  className="w-full px-4 py-2 bg-[#15121e] border border-white/10 rounded-lg text-white focus:outline-none focus:border-violet-500"
                >
                  <option value="user">Usuario</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>

              <div>
                <Label className="text-gray-300">Nueva contraseña (opcional)</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={editForm.new_password}
                    onChange={(e) => setEditForm({...editForm, new_password: e.target.value})}
                    placeholder="Dejar vacío para no cambiar"
                    className="bg-[#15121e] border-white/10 text-white pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-gray-500 text-xs mt-1">Mínimo 6 caracteres si se cambia</p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 p-6 border-t border-white/10">
              <Button
                variant="outline"
                onClick={() => setEditingUser(null)}
                className="flex-1 border-white/10 text-gray-300 hover:bg-white/5"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveUser}
                disabled={savingUser}
                className="flex-1 bg-violet-500 hover:bg-violet-600 text-white"
              >
                {savingUser ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de edición de plaza */}
      {editingSpot && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#231f30] rounded-xl p-6 w-full max-w-lg mx-4 border border-white/10">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Editar Plaza</h3>
              <button
                onClick={() => setEditingSpot(null)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-1">Título</label>
                <input
                  type="text"
                  value={spotForm.title}
                  onChange={(e) => setSpotForm({...spotForm, title: e.target.value})}
                  className="w-full px-4 py-2.5 bg-[#15121e] border border-white/10 rounded-lg text-white focus:outline-none focus:border-violet-500"
                />
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-1">Descripción</label>
                <textarea
                  value={spotForm.description}
                  onChange={(e) => setSpotForm({...spotForm, description: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2.5 bg-[#15121e] border border-white/10 rounded-lg text-white focus:outline-none focus:border-violet-500 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Dirección</label>
                  <input
                    type="text"
                    value={spotForm.address}
                    onChange={(e) => setSpotForm({...spotForm, address: e.target.value})}
                    className="w-full px-4 py-2.5 bg-[#15121e] border border-white/10 rounded-lg text-white focus:outline-none focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Ciudad</label>
                  <input
                    type="text"
                    value={spotForm.city}
                    onChange={(e) => setSpotForm({...spotForm, city: e.target.value})}
                    className="w-full px-4 py-2.5 bg-[#15121e] border border-white/10 rounded-lg text-white focus:outline-none focus:border-violet-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Precio/hora (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={spotForm.price_hour}
                    onChange={(e) => setSpotForm({...spotForm, price_hour: e.target.value})}
                    className="w-full px-4 py-2.5 bg-[#15121e] border border-white/10 rounded-lg text-white focus:outline-none focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Precio/día (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={spotForm.price_day}
                    onChange={(e) => setSpotForm({...spotForm, price_day: e.target.value})}
                    className="w-full px-4 py-2.5 bg-[#15121e] border border-white/10 rounded-lg text-white focus:outline-none focus:border-violet-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Precio/mes (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={spotForm.price_month}
                    onChange={(e) => setSpotForm({...spotForm, price_month: e.target.value})}
                    className="w-full px-4 py-2.5 bg-[#15121e] border border-white/10 rounded-lg text-white focus:outline-none focus:border-violet-500"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setEditingSpot(null)}
                className="flex-1 border-white/10 text-gray-300 hover:bg-white/5"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveSpot}
                disabled={savingSpot}
                className="flex-1 bg-violet-500 hover:bg-violet-600 text-white"
              >
                {savingSpot ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Guardar
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Floating bulk action bar */}
      {(activeTab === "users" && selectedUserIds.length > 0) && (
        <div
          data-testid="admin-users-bulk-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-[#231f30] border border-violet-500/30 rounded-2xl shadow-2xl shadow-violet-500/20 px-5 py-3 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-4 duration-200"
        >
          <div className="flex items-center gap-2 text-white">
            <span className="w-6 h-6 rounded-full bg-violet-500 flex items-center justify-center text-xs font-bold">
              {selectedUserIds.length}
            </span>
            <span className="text-sm">usuario(s) seleccionado(s)</span>
          </div>
          <div className="h-6 w-px bg-white/10" />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSelectedUserIds([])}
            className="text-gray-400 hover:text-white"
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={() => setBulkDeleteTarget("users")}
            className="bg-red-500/90 hover:bg-red-500 text-white"
            data-testid="admin-users-bulk-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Eliminar seleccionados
          </Button>
        </div>
      )}
      {(activeTab === "spots" && selectedSpotIds.length > 0) && (
        <div
          data-testid="admin-spots-bulk-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-[#231f30] border border-violet-500/30 rounded-2xl shadow-2xl shadow-violet-500/20 px-5 py-3 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-4 duration-200"
        >
          <div className="flex items-center gap-2 text-white">
            <span className="w-6 h-6 rounded-full bg-violet-500 flex items-center justify-center text-xs font-bold">
              {selectedSpotIds.length}
            </span>
            <span className="text-sm">plaza(s) seleccionada(s)</span>
          </div>
          <div className="h-6 w-px bg-white/10" />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSelectedSpotIds([])}
            className="text-gray-400 hover:text-white"
          >
            Cancelar
          </Button>
          <Button
            size="sm"
            onClick={() => setBulkDeleteTarget("spots")}
            className="bg-red-500/90 hover:bg-red-500 text-white"
            data-testid="admin-spots-bulk-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Eliminar seleccionadas
          </Button>
        </div>
      )}

      {/* Bulk delete confirmation modal */}
      <Dialog open={!!bulkDeleteTarget} onOpenChange={(open) => !open && setBulkDeleteTarget(null)}>
        <DialogContent className="bg-[#231f30] border-white/10 text-white" data-testid="bulk-delete-modal">
          <DialogHeader>
            <DialogTitle className="text-white">
              {bulkDeleteTarget === "users"
                ? `¿Eliminar ${selectedUserIds.length} usuario(s)?`
                : `¿Eliminar ${selectedSpotIds.length} plaza(s)?`}
            </DialogTitle>
            <DialogDescription className="text-gray-400 pt-2">
              {bulkDeleteTarget === "users"
                ? "Se eliminarán también sus plazas publicadas y sus reservas. Esta acción es irreversible."
                : "Se cancelarán las reservas pendientes de estas plazas. Esta acción es irreversible."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setBulkDeleteTarget(null)}
              disabled={bulkDeleting}
              className="border-white/10 text-gray-300 hover:bg-white/5"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="bulk-delete-confirm-btn"
            >
              {bulkDeleting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Eliminando...</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" /> Confirmar eliminación</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
